import React from 'react'
import { motion } from 'framer-motion';
import { Sparkles, Calendar, MapPin, Loader2 } from 'lucide-react';
import { cn } from '@city-sage/aurum-avis-labs-components';
import { MarkdownRenderer } from '../MarkdownRenderer';
import { Image } from "@city-sage/aurum-avis-labs-components";

interface BaseToolCardProps {
  status: 'loading' | 'complete';
  onExitComplete?: () => void;
}

interface ItineraryToolCardProps extends BaseToolCardProps {
  title?: string;
  shortDescription?: string;
  imageUrl?: string;
}

interface DayToolCardProps extends BaseToolCardProps {
  title?: string;
  description?: string;
  dayNumber?: number;
  itineraryImageUrl?: string;
}

interface StopToolCardProps extends BaseToolCardProps {
  title?: string;
  highlight?: string;
  description?: string;
  imageUrl?: string;
  index?: number;
}

const cardVariants = {
  hidden: { 
    opacity: 0, 
    y: 20,
    scale: 0.95
  },
  visible: { 
    opacity: 1, 
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 25,
      duration: 0.5,
    }
  },
  exit: {
    opacity: 0,
    y: -20,
    scale: 0.95,
    transition: {
      duration: 0.3,
      ease: "easeInOut"
    }
  }
};

export function ItineraryToolCard({ 
  status, 
  title, 
  shortDescription, 
  imageUrl,
  onExitComplete 
}: ItineraryToolCardProps) {
  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      onAnimationComplete={(definition) => {
        if (definition === "exit" && onExitComplete) {
          onExitComplete();
        }
      }}
      className="flex items-center gap-3 p-4 rounded-2xl bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 shadow-sm"
    >
      <div className="relative flex-shrink-0">
        <div className="h-14 w-14 rounded-xl overflow-hidden bg-muted flex items-center justify-center shadow-sm">
          {imageUrl ? (
            <Image 
              src={imageUrl} 
              alt={title || 'Itinerary'} 
              className="h-full w-full"
              imageClassName="h-full w-full object-cover"
            />
          ) : null}
          <Sparkles className={cn(
            "h-7 w-7 text-primary",
            imageUrl && "hidden"
          )} />
        </div>
        {status === 'loading' && (
          <motion.div 
            className="absolute -top-1 -right-1 h-5 w-5 bg-primary rounded-full flex items-center justify-center shadow-sm"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            <Loader2 className="h-3 w-3 text-primary-foreground animate-spin" />
          </motion.div>
        )}
        {status === 'complete' && (
          <motion.div 
            className="absolute -top-1 -right-1 h-5 w-5 bg-green-500 rounded-full flex items-center justify-center shadow-sm"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 500, damping: 30 }}
          >
            <div className="h-2 w-2 bg-white rounded-full" />
          </motion.div>
        )}
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <motion.span 
            className="text-xs font-semibold text-primary uppercase tracking-wide"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            {status === 'loading' ? 'Creating Itinerary' : 'Itinerary Created'}
          </motion.span>
        </div>
        <motion.h4 
          className="font-semibold text-sm text-foreground truncate"
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
        >
          {title || 'New Itinerary'}
        </motion.h4>
        {shortDescription && (
          <motion.p 
            className="text-xs text-muted-foreground line-clamp-1 mt-0.5"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
          >
            <MarkdownRenderer content={shortDescription} className="text-xs text-muted-foreground" />
          </motion.p>
        )}
      </div>
    </motion.div>
  );
}

export function DayToolCard({ 
  status, 
  title, 
  description, 
  dayNumber, 
  itineraryImageUrl,
  onExitComplete 
}: DayToolCardProps) {
  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      onAnimationComplete={(definition) => {
        if (definition === "exit" && onExitComplete) {
          onExitComplete();
        }
      }}
      className="flex items-center gap-3 p-4 rounded-2xl bg-gradient-to-r from-blue-500/10 to-blue-500/5 border border-blue-500/20 shadow-sm"
    >
      <div className="relative flex-shrink-0">
        <div className="h-14 w-14 rounded-xl overflow-hidden bg-muted flex items-center justify-center shadow-sm">
          {itineraryImageUrl ? (
            <Image 
              src={itineraryImageUrl} 
              alt={title || 'Day'} 
              className="h-full w-full"
              imageClassName="h-full w-full object-cover"
            />
          ) : null}
          <Calendar className={cn(
            "h-7 w-7 text-blue-600",
            itineraryImageUrl && "hidden"
          )} />
        </div>
        {status === 'loading' && (
          <motion.div 
            className="absolute -top-1 -right-1 h-5 w-5 bg-blue-600 rounded-full flex items-center justify-center shadow-sm"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            <Loader2 className="h-3 w-3 text-white animate-spin" />
          </motion.div>
        )}
        {status === 'complete' && (
          <motion.div 
            className="absolute -top-1 -right-1 h-5 w-5 bg-green-500 rounded-full flex items-center justify-center shadow-sm"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 500, damping: 30 }}
          >
            <div className="h-2 w-2 bg-white rounded-full" />
          </motion.div>
        )}
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <motion.span 
            className="text-xs font-semibold text-blue-600 uppercase tracking-wide"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            {status === 'loading' ? 'Adding Day' : 'Day Added'}
          </motion.span>
          {dayNumber && (
            <motion.span 
              className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.4, type: "spring", stiffness: 500 }}
            >
              Day {dayNumber}
            </motion.span>
          )}
        </div>
        <motion.h4 
          className="font-semibold text-sm text-foreground truncate"
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
        >
          {title || 'New Day'}
        </motion.h4>
        {description && (
          <motion.p 
            className="text-xs text-muted-foreground line-clamp-1 mt-0.5"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
          >
            {description}
          </motion.p>
        )}
      </div>
    </motion.div>
  );
}

export function StopToolCard({ 
  status, 
  title, 
  highlight, 
  description, 
  imageUrl, 
  index,
  onExitComplete 
}: StopToolCardProps) {
  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      onAnimationComplete={(definition) => {
        if (definition === "exit" && onExitComplete) {
          onExitComplete();
        }
      }}
      className="flex items-center gap-3 p-4 rounded-2xl bg-gradient-to-r from-green-500/10 to-green-500/5 border border-green-500/20 shadow-sm"
    >
      <div className="relative flex-shrink-0">
        <div className="h-14 w-14 rounded-xl overflow-hidden bg-muted flex items-center justify-center shadow-sm">
          {imageUrl ? (
            <Image
              src={imageUrl} 
              alt={title || 'Stop'} 
              className="h-full w-full"
              imageClassName="h-full w-full object-cover"
            />
          ) : null}
          <MapPin className={cn(
            "h-7 w-7 text-green-600",
            imageUrl && "hidden"
          )} />
        </div>
        {status === 'loading' && (
          <motion.div 
            className="absolute -top-1 -right-1 h-5 w-5 bg-green-600 rounded-full flex items-center justify-center shadow-sm"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            <Loader2 className="h-3 w-3 text-white animate-spin" />
          </motion.div>
        )}
        {status === 'complete' && (
          <motion.div 
            className="absolute -top-1 -right-1 h-5 w-5 bg-green-500 rounded-full flex items-center justify-center shadow-sm"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 500, damping: 30 }}
          >
            <div className="h-2 w-2 bg-white rounded-full" />
          </motion.div>
        )}
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <motion.span 
            className="text-xs font-semibold text-green-600 uppercase tracking-wide"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            {status === 'loading' ? 'Adding Stop' : 'Stop Added'}
          </motion.span>
          {index && (
            <motion.span 
              className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.4, type: "spring", stiffness: 500 }}
            >
              Stop {index}
            </motion.span>
          )}
        </div>
        <motion.h4 
          className="font-semibold text-sm text-foreground truncate"
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
        >
          {title || 'New Stop'}
        </motion.h4>
        {(highlight || description) && (
          <motion.p 
            className="text-xs text-muted-foreground line-clamp-1 mt-0.5"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
          >
            {highlight || description}
          </motion.p>
        )}
      </div>
    </motion.div>
  );
} 